"use client"

import { useState, useEffect } from "react";

import Layout from "../components/Layout"
import Card from "../components/Card"
import { Users, Package, CheckCircle, Clock } from "react-feather"
import { Link } from "react-router-dom"
import { formatDistanceToNow } from "date-fns"

const Home = () => {
  const [stats, setStats] = useState({
    activeUsers: 0,
    pendingApprovals: 0,
    totalMedicines: 0,
    recentActivity: [],
  })
  const [isLoading, setIsLoading] = useState(true)
  const [showAllActivities, setShowAllActivities] = useState(false)
  const isLoggedIn = localStorage.getItem("userToken") !== null
  const API_BASE_URL = 'https://api.tenarx.net';

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      try {
        const token = localStorage.getItem("userToken")
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        const [userStatsRes, medicineStatsRes, recentActivitiesRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/analytics/user-stats`, { headers }),
          fetch(`${API_BASE_URL}/api/analytics/medicine-stats`, { headers }),
          fetch(`${API_BASE_URL}/api/analytics/recent-activities`, { headers }),
        ])
        const userStatsData = await userStatsRes.json()
        const medicineStatsData = await medicineStatsRes.json()
        console.log(medicineStatsData)
        const recentActivitiesData = await recentActivitiesRes.json()

        setStats({
          activeUsers: userStatsData?.data?.active || 0,
          pendingApprovals: userStatsData?.data?.pending || 0,
          totalMedicines: medicineStatsData?.data?.stats?.total || 0,
          recentActivity: (recentActivitiesData?.data || []).map((item, idx) => {
            let action = ""
            if (item.type === "user") {
              action = `New user registered (${item.role})`;
            } else if (item.type === "pharmacy_medicine") {
              action = `Pharmacy medicine added: ${item.name}`;
            } else if (item.type === "supplier_medicine") {
              action = `Supplier medicine added: ${item.name}`;
            }
            return {
              id: item._id || idx,
              action,
              time: item.createdAt ? formatDistanceToNow(new Date(item.createdAt), { addSuffix: true }) : "-",
            }
          }),
        })
      } catch (error) {
        console.error("Error fetching dashboard data:", error)
      } finally {
        setIsLoading(false)
      }
    }

    if (isLoggedIn) {
      fetchData()
    } else {
      setIsLoading(false)
    }
  }, [isLoggedIn])

  const StatCard = ({ title, value, icon: Icon, color, linkTo }) => {
    return (
      <Card className="flex flex-col">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500">{title}</p>
            <h3 className="text-2xl font-bold mt-1">{isLoading ? "-" : value}</h3>
          </div>
          <div className={`p-3 rounded-full ${color}`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
        {linkTo && (
          <Link to={linkTo} className="text-sm text-blue-600 hover:underline mt-4 inline-block">
            View details →
          </Link>
        )}
      </Card>
    )
  }

  return (
    <Layout title="Dashboard">
      {!isLoggedIn ? (
        <div className="flex flex-col items-center justify-center h-[80vh] text-center">
          <h2 className="text-2xl font-bold mb-4">Welcome to MedTrack</h2>
          <p className="text-gray-600 mb-8 max-w-md">
            Please log in to access the dashboard and manage your medicines and approvals.
          </p>
          <div className="flex gap-4">
            <Link to="/" className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">
              Login
            </Link>
            <Link
              to="/signup"
              className="px-5 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
            >
              Sign Up
            </Link>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            <StatCard
              title="Active Users"
              value={stats.activeUsers}
              icon={Users}
              color="bg-blue-500"
              linkTo="/active"
            />
            <StatCard
              title="Pending Approvals"
              value={stats.pendingApprovals}
              icon={Clock}
              color="bg-yellow-500"
              linkTo="/approvals"
            />
            <StatCard
              title="Total Medicines"
              value={stats.totalMedicines}
              icon={Package}
              color="bg-green-500"
              linkTo="/allMedicine"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card title="Recent Activity">
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : (
                <>
                  <ul className="divide-y divide-gray-200">
                    {(showAllActivities ? stats.recentActivity : stats.recentActivity.slice(0, 5)).map((activity) => (
                      <li key={activity.id} className="py-3 flex justify-between">
                        <span>{activity.action}</span>
                        <span className="text-gray-500 text-sm">{activity.time}</span>
                      </li>
                    ))}
                  </ul>
                  {stats.recentActivity.length > 5 && (
                    <button
                      className="mt-3 text-blue-600 hover:underline text-sm"
                      onClick={() => setShowAllActivities((prev) => !prev)}
                    >
                      {showAllActivities ? "Show less" : "Show all"}
                    </button>
                  )}
                </>
              )}
            </Card>

            <Card title="Quick Actions">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                <Link to="/approvals" className="p-4 border rounded-lg hover:bg-gray-50 flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-full">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-medium">Review Approvals</h3>
                    <p className="text-sm text-gray-500">Check pending requests</p>
                  </div>
                </Link>

                <Link to="/medicine" className="p-4 border rounded-lg hover:bg-gray-50 flex items-center gap-3">
                  <div className="p-2 bg-yellow-100 rounded-full">
                    <Package className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div>
                    <h3 className="font-medium">View Medicines</h3>
                    <p className="text-sm text-gray-500">Browse all medicines</p>
                  </div>
                </Link>
              </div>
            </Card>
          </div>
        </>
      )}
    </Layout>
  )
}

export default Home
